/**
 * MobileClientsView
 *
 * Mobile-optimized version of ClientsView.
 * Renders when isMobile === 2.
 * Uses MUI Tabs for view switching (Clients / Équipes / Sous-traitants)
 * and a top menu bar matching the project's mobile patterns.
 *
 * All original functionality is preserved:
 * - Three tabs: Clients (0), Teams (1), Subcontractors (2)
 * - URL-param-driven view switching via navigate()
 * - Add button for Teams view (shown in top menu)
 * - Data fetching on mount (collabs, clients, teams, subcontractors, postes)
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Langs from "../../../app/lang/langs";
import { getCollabsNames } from "../../../components/vcomp/vcompSlice";
import { getClients, getSubcontractors } from "../../../components/clients/clientsSlice";
import { getTeams } from "../../../components/teams/teamsSlice";
import { getAllCollabs, getPostes } from "../../../components/collabs/collabsSlice";
import MobileClientsTab from "./MobileClientsTab";
import MobileSubcontractorsTab from "./MobileSubcontractorsTab";
import MobileTeamsTab from "./MobileTeamsTab";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.32s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "14px",
  color: "rgba(0,0,0,0.6)",
  transition: TRANSITION_SMOOTH,
  backdropFilter: "blur(8px)",
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.1)",
    color: "rgb(255,144,21)",
    boxShadow: "0 4px 16px rgba(255,144,21,0.2), 0 1px 4px rgba(255,144,21,0.1)",
    transform: "scale(1.08)",
  },
  "&:active": {
    transform: "scale(0.94)",
    boxShadow: "0 1px 4px rgba(255,144,21,0.1)",
  },
};

export default function MobileClientsView() {
  const mounted = useRef(null);
  const dispatch = useDispatch();
  const urlParams = useParams();
  const navigate = useNavigate();

  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);

  const viewsOptions = [
    { label: <Langs str="CLIENTS" />, value: 0 },
    { label: <Langs str="TEAMS" />, value: 1 },
    { label: <Langs str="SUBCONTRACTORS" />, value: 2 },
  ];

  const [selectedView, setSelectedView] = useState(0);

  /* ---- Data fetching ---- */
  useEffect(() => {
    if (!mounted.current) {
      dispatch(getCollabsNames());
      dispatch(getClients());
      dispatch(getTeams());
      dispatch(getAllCollabs());
      dispatch(getPostes());
      dispatch(getSubcontractors());
    }
    mounted.current = true;
  }, [mounted]);

  /* ---- URL param sync ---- */
  useEffect(() => {
    if (urlParams.subView && viewsOptions[urlParams.subView]) {
      setSelectedView(Number(urlParams.subView));
    }
  }, [urlParams]);

  /* ---- Navigation handler ---- */
  const handleViewChange = (_event, newValue) => {
    if (newValue != null) {
      let selectedTeamId = selectedTeamsIds[0];
      navigate(`/clients/${selectedTeamId}/${newValue}`);
    }
  };

  /* ---- Body renderer ---- */
  const renderBody = () => {
    if (selectedView === 0 || selectedView === "0") {
      return <MobileClientsTab />;
    }
    if (selectedView === 1 || selectedView === "1") {
      return <MobileTeamsTab />;
    }
    if (selectedView === 2 || selectedView === "2") {
      return <MobileSubcontractorsTab />;
    }
    return null;
  };

  return (
    <Box sx={{ mt: 0, display: "flex", flexDirection: "column", height: "100vh", bgcolor: "rgba(245,246,250,0.97)" }}>
      {/* Top menu */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          bgcolor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
          borderBottom: "1px solid rgba(0,40,128,0.04)",
          boxShadow:
            "0 8px 32px -8px rgba(0,40,128,0.06), 0 2px 8px -2px rgba(0,0,0,0.03)",
          px: 1.5,
          pt: 0.75,
          pb: 0,
          transition: TRANSITION_SMOOTH,
          position: "relative",
          zIndex: 10,
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: "5%",
            right: "5%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(255,144,21,0.2), rgba(255,193,0,0.15), transparent)",
          },
        }}
      >
        {/* Title row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 52,
            px: 0.5,
          }}
        >
          <Box
            sx={{
              fontWeight: 800,
              fontSize: "1.2rem",
              color: "rgba(0,0,0,0.88)",
              pl: 0.5,
              letterSpacing: "-0.03em",
              transition: "color 0.25s ease",
              background: "linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(0,40,128,0.7) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {viewsOptions.find((o) => o.value == selectedView)?.label}
          </Box>

        </Box>

        {/* Tabs row */}
        <Tabs
          value={Number(selectedView) || 0}
          onChange={handleViewChange}
          variant="fullWidth"
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              minWidth: 0,
              px: 1.5,
              py: 0.5,
              fontSize: "0.82rem",
              fontWeight: 600,
              textTransform: "none",
              color: "rgba(255,144,21,0.5)",
              borderRadius: "12px 12px 0 0",
              transition: TRANSITION_SMOOTH,
              letterSpacing: "-0.01em",
              "&:hover": {
                color: "rgba(255,144,21,0.85)",
                backgroundColor: "rgba(255,144,21,0.06)",
              },
              "&.Mui-selected": {
                fontWeight: 700,
                color: "rgb(255,144,21)",
                textShadow: "0 0 20px rgba(255,144,21,0.15)",
              },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              background: "linear-gradient(90deg, #FF8701, #FFC100)",
              boxShadow: "0 0 12px rgba(255,144,21,0.35), 0 0 4px rgba(255,193,0,0.2)",
              transition: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
          }}
        >
          {viewsOptions.map((option) => (
            <Tab key={option.value} label={option.label} value={option.value} />
          ))}
        </Tabs>
      </Box>

      {/* Body content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {renderBody()}
      </Box>
    </Box>
  );
}
